/**
 * The MEANING map (strict grammar, ruled 2026-08-06): every semantic
 * color in the product resolves HERE, and both worlds read it -- the
 * strata components and the hand-rendered canvas (the canvas is a
 * projection of the grammar, not an exception). A hex that means
 * something never appears anywhere else.
 *
 * The map is a FUNCTION of the palette in force (docs/STRATA.md): the
 * meaning is fixed, the hex it resolves to follows the mode.
 */
import { usePalette, type Hex, type Palette } from '../theme';

export type VerdictState = 'complying' | 'violating';
export type DeltaBucket = 'added' | 'modified' | 'removed' | 'unchanged';

export interface Paints {
  /** Compliance is binary. Violating's red is the ONLY red that means
   * "breach" -- redline geography, findings, counts, all of it. */
  readonly verdict: Readonly<Record<VerdictState, Hex>>;
  /** Absence is loud but it is NOT violation: UNPRICED, UNVERIFIED,
   * NULL, proxy classifications all wear this one amber. */
  readonly absence: Hex;
  /** Element identity: color follows the entity's category, never its
   * rank. One palette for the canvas, the legend, the overview dots,
   * and the review queue -- a category never changes clothes. */
  readonly category: ReadonlyMap<string, Hex>;
  readonly categoryDefault: Hex;
  /** Revision deltas (the diff module's four buckets). */
  readonly delta: Readonly<Record<DeltaBucket, Hex>>;
  /** The action paint: exports, links, the active revision. */
  readonly action: Hex;
}

export function paintsOf(p: Palette): Paints {
  return {
    verdict: { complying: p.complying, violating: p.violating },
    absence: p.absence,
    category: new Map([
      ['ELECTRICAL', p.categorical[0]],
      ['PLUMBING', p.categorical[1]],
      ['MECHANICAL', p.categorical[2]],
      ['STRUCTURAL', p.categorical[3]],
      ['ARCHITECTURAL', p.categorical[4]],
    ]),
    categoryDefault: p.inkMuted,
    delta: { added: p.complying, modified: p.absence, removed: p.red, unchanged: p.inkMuted },
    action: p.blue,
  };
}

/** The meaning map for the mode in force. */
export function usePaints(): Paints {
  return paintsOf(usePalette());
}

/** The paint of an element's category, or the default for an unknown one. */
export function categoryPaint(paints: Paints, category: string | null | undefined): Hex {
  return (category ? paints.category.get(category) : undefined) ?? paints.categoryDefault;
}

/** The paint of a compliance verdict. */
export function verdictPaint(paints: Paints, state: VerdictState): Hex {
  switch (state) {
    case 'complying':
      return paints.verdict.complying;
    case 'violating':
      return paints.verdict.violating;
  }
}
